import { createCore } from './utils/create-core'

import Vue from 'vue'
import store from './store'
import i18n from './plugins/i18n'
import VueRouter from 'vue-router'
import BootstrapVue from 'bootstrap-vue'
import loadAppConfig from './loadConfig'
import loadClient from './loadClientFake'
import LayoutLoader from './LayoutLoader'
import VueFormWizard from 'vue-form-wizard'
import AuthProvider from './lib/auth/provider'
import SocialShare from './components/SocialShare'

import { VueInstanceStorePlugin } from './utils/vuex'
import { library } from '@fortawesome/fontawesome-svg-core'
import { getLanguage, setUserDefinedFunctions, clearCookie } from './utils'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCoffee, faLock, faUnlock } from '@fortawesome/free-solid-svg-icons'
import VueHighlightJS from 'vue-highlightjs'
import { Icon } from '@iconify/vue2'

library.add(faCoffee, faLock, faUnlock)

import qs from 'qs'
import ElementUI from 'element-ui'
import VeeValidate from 'vee-validate'
import fullscreen from 'vue-fullscreen'
import EventBus from './utils/event-bus'
import DynForm from './components/DynForm'
import ClickOutside from 'vue-click-outside'
import validationConfig from './utils/validation-config'

import { get, isNil, merge, isEmpty, omit } from 'lodash'
import { getCookie, setCookie } from 'tiny-cookie'
import { appendHelpyComponent, loadHelpy } from './helpy'
import { resolveSeoRoute } from './utils/resolve-seo-urls'
import { extendValidator } from './utils/validation-extend-rules'
import { init as initNativeScript } from './plugins/nativescript'

import file from './utils/file'
import axios from './axiosClient'
import locale from 'element-ui/lib/locale'
import Loader from './components/Loader.vue'
import element_en from 'element-ui/lib/locale/lang/en'
import element_hu from 'element-ui/lib/locale/lang/hu'

import './assets/sass/default.css'
import 'vue-form-wizard/dist/vue-form-wizard.min.css'
import { ErrorBoundary, errorBoundaryOptions } from './components/ErrorBoundary'
import moment from 'moment/moment'
import { COOKIE_OPTIONS } from './lib/cookies/cookies'
import fetchClient from './fetchClient'

import * as Sentry from '@sentry/vue'

let CORE = {}
let config = {}
let initialized = false

const cachedUrls = {
  urls: [],
  publicUrls: [],
  ifModifiedSince: '',
}

function prepareUrl(str) {
  return str.replace(/^\/*/, '').replace(/[\?#].*/gm, "$'") // @regex levagja a string elejerol a / karaktereket es a vegerol a # vagy ? el kezdodo reszt
}

function isUrlPublic(path) {
  return get(cachedUrls, 'publicUrls', []).includes(prepareUrl(path))
}

function getCurrentPath() {
  return `${document.location.pathname}${document.location.search}`
}

let seoUrls = []
let currentPath = getCurrentPath()
const HTTP_UNAUTHORIZED_ERROR = 401

/**
 * @description User autentikáás minden oldal betöltés vagy routing-kor
 * @param resolvedPath: string
 * @param backgroundAuthCheck: boolean
 * @returns {Promise<{}>}
 */
async function authenticate(resolvedPath, backgroundAuthCheck = true) {
  const authenticator = AuthProvider.getAuthenticator()

  // ha létezik az url, és nem publikus, akkor autentikálunk
  if (!isUrlPublic(resolvedPath) || resolvedPath.includes('#state=')) {
    try {
      if (backgroundAuthCheck) {
        const authResponse = await axios().get('/api/Authenticators/authenticate')
        const isAuthenticated = get(authResponse, 'data.authenticated')
        if (!isAuthenticated) {
          await authenticator?.login()
          await runLoginSuccessProcess()
        }
      } else {
        const isAuthenticated = await authenticator?.login()

        if (isAuthenticated) {
          setInterval(async () => {
            await authenticator.refreshToken()
          }, 5000)
        }

        const isLoggedIn = getCookie('isLoggedIn') === 'true'
        if (!isLoggedIn) {
          await runLoginSuccessProcess()
          setCookie('isLoggedIn', true, {
            expires: '1D',
            ...COOKIE_OPTIONS,
          })
        }
      }
    } catch (error) {
      console.error('Failed to authenticate user:', error)
      return (window.location = `/403.html`)
    }
  }
}

function checkIfUrlExists(path) {
  const authenticator = AuthProvider.getAuthenticator()
  const existingUrl = cachedUrls.urls.find((url) => url.route === prepareUrl(path))
  if (!existingUrl) {
    const logoutUrl = authenticator?.getLogoutUrl(document.location.href)
    window.location = `/404.html${logoutUrl ? `?logout-url=${encodeURIComponent(logoutUrl)}` : ''}`
  }
}

/**
 * @description LoginSuccess process meghívása sikeres bejelentkezés után
 */
async function runLoginSuccessProcess() {
  const checkinResponse = await axios().get('/api/SUsers/loginSuccess')
  const redirectUrl = get(checkinResponse, 'headers.location')

  if (redirectUrl && redirectUrl !== '/?failed') window.location = redirectUrl
}

/**
 * @description Before Action típusú processzek meghívása oldalbetöltés előtt
 * @param path: string
 * @param query: object
 * @returns {Promise<{}>}
 */
async function runBeforeActionProcess(path, query) {
  const params = JSON.stringify(new URLSearchParams(query))
  const instance = JSON.stringify({ url: path })

  let beforeActionResponse
  try {
    beforeActionResponse = await axios().get(`/api/ProcessBuilders/emitEvent`, {
      params: {
        event: 'beforeAction',
        instance,
        params,
      },
    })
  } catch (error) {
    console.error(error)
  }

  const redirectUrl = get(beforeActionResponse, 'headers.location')
  if (redirectUrl) {
    window.location.href = redirectUrl
  }
}

async function setFrontendConfig() {
  if (isEmpty(config)) {
    let configFilePath = `/static/config.json`
    const configResponse = await fetch(configFilePath)
    config = await configResponse.json()
  }
  window.config = config
}

/**
 * @description A grafikus interfészünk alapja
 * @returns {Promise<string|string|*>}
 */
async function main() {
  Vue.use(VueRouter)

  const router = new VueRouter({
    mode: 'history',
    routes: [
      {
        path: '*',
        component: LayoutLoader,
      },
    ],
    parseQuery(query) {
      return qs.parse(query)
    },
    stringifyQuery(query) {
      const result = qs.stringify(query)
      return result ? '?' + result : ''
    },
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      } else {
        return { x: 0, y: 0 }
      }
    },
  })

  if (['', '/'].includes(currentPath)) {
    return (window.location.href = config.landingPage)
  }

  try {
    const urlsResponse = await axios().get(`/api/Urls/cached-all-urls`)
    const seoUrlsResponse = await axios().get(`/api/Urls/get-seo-urls`)
    const isModifiedSince = get(urlsResponse, 'data.ifModifiedSince', [])
    if (isModifiedSince) {
      cachedUrls.urls = get(urlsResponse, 'data.urls', [])
      cachedUrls.ifModifiedSince = isModifiedSince
      cachedUrls.publicUrls = cachedUrls.urls.filter((url) => url.isPublic).map((url) => url.route)
    }
    seoUrls = get(seoUrlsResponse, 'data', [])
  } catch (e) {
    console.error(e)
  }

  const { url: resolvedUrl } = await resolveSeoRoute(currentPath, seoUrls)
  let resolvedPath = resolvedUrl.split('?')[0]

  // ha nem létezik az url -> 404
  checkIfUrlExists(resolvedPath)

  /*  AUTHENTICATE USER */
  await authenticate(resolvedPath, false)

  await runBeforeActionProcess(resolvedUrl, {})

  const fullConfigResults = await axios().get(`/api/ConfigManagers/get-config`, {
    params: { url: resolvedUrl },
  })
  const fullConfig = get(fullConfigResults, 'data', {})
  const core = await createCore(fullConfig)
  const alreadyLoadedInHeader = {
    styles: {},
    scripts: {},
  }
  CORE = Object.assign({ alreadyLoadedInHeader }, core, fullConfig, { intervalTimers: [] })

  notifyMobile(CORE)
  //sneaky(CORE)

  extendValidator()

  Vue.component('fas-icon', FontAwesomeIcon)
  Vue.component('social-share', SocialShare)
  Vue.component('dyn-form', DynForm)
  Vue.component('icon', Icon)

  Vue.directive('click-outside', ClickOutside)
  Vue.directive('plaintext', {
    bind(el, binding) {
      el.innerHTML = el.innerText
    },
  })
  Vue.directive('loader', {
    bind(el, binding) {
      const loader = new Vue(Loader).$mount()
      el.appendChild(loader.$el)
      el.__loader = loader
      el.style.position = 'relative'
      el.__loader.$el.style.display = binding.value ? 'flex' : 'none'
    },
    update(el, binding) {
      el.__loader.$el.style.display = binding.value ? 'flex' : 'none'
    },
  })
  Vue.use(VueInstanceStorePlugin)
  Vue.use(BootstrapVue)
  Vue.use(fullscreen)
  Vue.use(VueFormWizard)
  Vue.use(VueHighlightJS)

  Vue.use(ElementUI)

  window.CORE = CORE
  window.DOTO = CORE

  window.ExternalParams = window.ExternalParams || {}
  window.Queries = window.Queries || {}
  window.Fields = window.Fields || {}
  window.EventBus = EventBus
  window.fetch = fetchClient

  setFavicon(CORE)

  const originalPush = router.push
  router.push = function push(location) {
    return originalPush.call(this, location).catch((err) => err)
  }

  router.beforeEach(async (to, from, next) => {
    if (!initialized) return next()
    const fullPath = get(to, 'fullPath', '')

    // Csak akkor frissítünk, ha az URL path része változik
    if (to.fullPath !== from.fullPath) {
      currentPath = fullPath
      for (const timer of window.CORE.intervalTimers) {
        clearInterval(timer)
      }
      window.CORE.intervalTimers = []

      const { url: resolvedUrl } = await resolveSeoRoute(currentPath, seoUrls)
      resolvedPath = resolvedUrl.split('?')[0]

      // ha nem létezik az url -> 404
      checkIfUrlExists(resolvedPath)

      /*  AUTHENTICATE USER */
      await authenticate(resolvedPath, true)

      await runBeforeActionProcess(resolvedPath, to.query)

      const fullConfigResults = await axios().get(`/api/ConfigManagers/get-config`, {
        params: {
          url: resolvedUrl,
        },
      })

      const fullConfig = get(fullConfigResults, 'data', {})
      try {
        const core = await createCore(fullConfig)
        CORE = merge({ alreadyLoadedInHeader }, core, fullConfig, { intervalTimers: [] })
        window.CORE = CORE
        window.DOTO = CORE
        Vue.prototype.$CORE = window.CORE
        Vue.prototype.$DOTO = window.CORE
        notifyMobile(CORE)
      } catch (error) {
        const status = get(error, 'response.status', 403)
        if (status === HTTP_UNAUTHORIZED_ERROR) {
          return false
        }
      }
    }
    next()
  })

  router.afterEach((to, from) => {
    Vue.nextTick(() => {
      setSeoKeys(CORE)
    })
  })

  if (CORE.sentry?.project) {

    const traceTargets = (CORE.sentry.traceTargets || '')
      .split(',')
      .map(target => target.trim())
      .map(target => target.startsWith('http') ? new RegExp(`^${target.replace(/\./g, '\\.')}`) : target) // @regex a .env string tömb regex tömbbé alakítása


    const sentryConfig = {
      Vue,
      dsn: CORE.sentry.dsn,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        Sentry.browserProfilingIntegration(),
      ],
      initialScope: (scope) => {
        scope.setTag('service', 'frontend')
        return scope
      },
      release: `${CORE.sentry.project}@${CORE.version}`,
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: traceTargets,
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
      // Profiling
      profilesSampleRate: 1.0, // Profile 100% of the transactions. This value is relative to tracesSampleRate
    }

    Sentry.init(sentryConfig)

    router.onError((error) => {
      Sentry.captureException(error)
    })
  }

  const LANGUAGE = applicationLanguage()
  const moment = require('moment')
  Vue.use(require('vue-moment'), {
    moment,
  })
  moment.locale(LANGUAGE)
  window.moment = moment // for expression-resolver eval

  switch (LANGUAGE) {
    case 'hu-HU':
      locale.use(element_hu)
      break
    default:
      locale.use(element_en)
  }

  let helpy = {}
  store.subscribe((mutation, state) => {
    if (mutation.type === 'LOAD_HELPY') {
      helpy = loadHelpy(state.helpy)
    }
  })

  Vue.use({
    install(Vue) {
      Vue.prototype.$CORE = CORE
      Vue.prototype.$DOTO = CORE
    },
  })

  let uuid = 0
  Vue.use((Vue) => {
    Vue.mixin({
      beforeCreate: function () {
        this.uuid = uuid.toString()
        uuid += 1
      },
      mounted: function () {
        appendHelpyComponent(helpy)
      },
    })

    Vue.prototype.$uid = function (id) {
      return `uid-${this.uuid}-${id}`
    }
  })

  const API_BASE_URL = `${CORE.apiBasePath}`
  const setup = {
    headers: {
      'Routed-Url': `${CORE.routedUrl}`,
      'Document-Policy': 'js-profiling',
    },
  }

  Promise.all([loadClient(setup)]).then(([client]) => {
    client.spec.basePath = `${API_BASE_URL}${client.spec.basePath}`
    loadAppConfig(API_BASE_URL, LANGUAGE, client, CORE.localizationLastModify).then((config) => {
      Vue.use(
        i18n,
        Object.assign(
          {
            simpleKey: true,
            defaultLocale: LANGUAGE,
          },
          config
        )
      )

      setUserDefinedFunctions(client)

      Vue.use({
        install(Vue, options) {
          Vue.prototype.$client = client
        },
      })

      Vue.use(VeeValidate, validationConfig, VueRouter)
      new Vue({
        router,
        store,
        render(h) {
          return h(ErrorBoundary, { ...errorBoundaryOptions }, [h(LayoutLoader, {})])
        },
      }).$mount('.core-app')
      initialized = true
      CORE.vueInitialized = true
    })
  })

  window.keycloakLogin = async function () {
    const authenticator = AuthProvider.getAuthenticator()
    await authenticator?.login()
  }

  window.keycloakLogout = function () {
    clearCookie('isLoggedIn')
    clearCookie('connect.sid')
    window.location = `${config.sso.keycloak.host}/realms/${config.sso.keycloak.realm}/protocol/openid-connect/logout?redirect_uri=${window.location.protocol}//${window.location.host}${CORE.landingPage}`
  }
}

await setFrontendConfig()

await main()

function sneaky(CORE) {
  const isEnvProduction = get(window, 'config.nodeEnv') === 'production'
  if (isEnvProduction) return
  Vue.config.productionTip = isEnvProduction
  CORE.isEnvProduction = isEnvProduction
  CORE.showSneaky = false

  if (!CORE.isEnvProduction) {
    Vue.config.devtools = true
    Vue.config.optionMergeStrategies.render = (parentVal, childVal) => {
      if (!childVal) return parentVal
      if (parentVal) {
        const mergedParentVal = function (...args) {
          args.push([childVal.apply(this, args)])
          return parentVal.apply(this, args)
        }
        return mergedParentVal
      }
      return childVal
    }

    window.addEventListener('keydown', (evt) => {
      if (evt.key === 'm' && evt.ctrlKey) {
        CORE.showSneaky = !CORE.showSneaky
        EventBus.$emit('sneaky-changed')
      }
    })
  }
}

function setFavicon(CORE) {
  const favicon = get(CORE, 'subSystem.favicon')
    ? file.methods.getStaticImageSource(CORE.subSystem.favicon)
    : '/static/favicon.ico'
  document.head.innerHTML += `<link rel='favicon' href='${favicon}'>
                              <link rel='shortcut icon' href='${favicon}'>`
}

function applicationLanguage() {
  let language = getCookie('language')
  if (!isNil(language)) {
    return language
  }
  language = getLanguage()
  console.log('set lang', COOKIE_OPTIONS)
  setCookie('language', language, {
    expires: '1Y',
    ...COOKIE_OPTIONS,
  })
  return language
}

function setSeoKeys(CORE) {
  const vue = new Vue()

  document.title = vue.$i18n.t(CORE.title)

  const description = document.getElementsByTagName('meta').namedItem('description')

  if (description) description.setAttribute('content', vue.$i18n.t(CORE.description))

  const keywords = document.getElementsByTagName('meta').namedItem('keywords')

  if (keywords) keywords.setAttribute('content', vue.$i18n.t(CORE.keywords))
}

async function catchUnauthorizedRequest(routedUrl) {
  window.open(document.location.origin + get(routedUrl, 'path', '/'), '_self', 'noopener, noreferrer')
}

// Set CORE object in mobile
function notifyMobile(coreObject) {
  if (window?.ReactNativeWebView) {
    const _CORE = omit(coreObject, ['CORE', 'socket'])
    window.ReactNativeWebView.postMessage(JSON.stringify({ eventName: 'SET_CORE', data: _CORE }))
  }
}
